import {useEffect, useState} from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import {MiniOrder, MiniTrade, Transaction} from '@App/redux/slices/reports/ReportsSlice'

import {Order, Trade} from "@Redux/slices/trade/trades/types"

import { replaceVarsInPath, ROUTE_NAMES } from '@UI/crypto_exchange/desktop/routes/routeNames'

import { useOnSuccessfulTrade } from '../../hooks/useOnSuccessfulTrade'

import { Analytics } from './components/Analytics'
import { EditOrderContainer } from './components/EditOrder'
import { EditTradeContainer } from './components/EditTrade'
import { Header } from './components/Header'
import { Filters } from './components/Header/components/Filters'
import { SendReportButton } from './components/Header/components/SendReportButton'
import { Tabs } from './components/Header/components/Tabs'
import { OrdersTable } from './components/OrdersTable'
import { SideBar } from './components/SideBar/Index'
import { TradeInfo } from './components/TradeInfo'
import { TradesTable } from './components/TradesTable'
import { TransactionsTable } from './components/TransactionsTable'
import { REPORT_STATUS_OPTIONS, REPORT_TYPE_OPTIONS, REPORTS_TABS, ReportsFilter, ReportsTabs } from './model'
import s from './styles.module.scss'
import { TransactionInfo } from './components/TransactionInfo'
import {subscribeToPublicService, unsubscribeToPublicService} from "@Redux/slices/sockets/PublicServiceConnectionSlice";
import {Events} from "@App/sockets/types";
import {useDispatch} from "react-redux";

enum SideBarTypes {
  Transactions = 1,
  Trades = 2,
  Orders = 3,
  TradesEdit = 4,
  OrdersEdit = 5,
}

export const Reports = () => {
  const { tab } = useParams<{ tab: ReportsTabs }>()
  const activeTab = tab ?? ReportsTabs.Transactions
  const navigate = useNavigate()
  const [openedSideBar, setOpenedSideBar] = useState<{
    title: string
    type: SideBarTypes
    data: Transaction
  } | null>(null)

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(subscribeToPublicService({ event: Events.GetTicker }))

    return () => {
      dispatch(unsubscribeToPublicService({ event: Events.GetTicker }))
    }
  }, [])

  const handleTransactionRowClick = (transaction: Transaction, title: string) => {
    setOpenedSideBar({ title: `${transaction.market.title} ${title}`, type: SideBarTypes.Transactions, data: transaction })
  }

  const handleTradeRowClick = (transaction: Transaction, title: string) => {
    setOpenedSideBar({ title: `${transaction.market.title} ${title}`, type: SideBarTypes.Trades, data: transaction })
  }

  const handleOrderRowClick = (transaction: Transaction, title: string) => {
    setOpenedSideBar({ title: `${transaction.market.title} ${title}`, type: SideBarTypes.Orders, data: transaction })
  }

  const handleEditTrade = () => {
    setOpenedSideBar((prev) => {
      if (!prev?.data) {
        return null
      }
      return { data: prev.data, title: 'Edit trade', type: SideBarTypes.TradesEdit }
    })
  }
  const handleEditOrder = () => {
    setOpenedSideBar((prev) => {
      if (!prev?.data) {
        return null
      }
      return { data: prev.data, title: 'Edit order', type: SideBarTypes.OrdersEdit }
    })
  }
  const setTab = (tab: ReportsTabs) => {
    setOpenedSideBar(null)
    navigate(replaceVarsInPath(ROUTE_NAMES.REPORTS, [tab]))
  }
  const activeTabOptions = REPORTS_TABS.find((v) => v.value === activeTab)
  const [filters, setFilters] = useState<ReportsFilter>(() => ({
    type: [...REPORT_TYPE_OPTIONS.map((v) => v.value)],
    period: '1m',
    status: [...REPORT_STATUS_OPTIONS.map((v) => v.value)],
  }))
  const setFilter = <K extends keyof ReportsFilter>(key: K, value: ReportsFilter[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }
  useOnSuccessfulTrade(() => {
    setOpenedSideBar(null)
  })

  return (
    <div className={s.container}>
      <Header
        tabsSelector={<Tabs activeTab={activeTab} setActiveTab={setTab} />}
        filters={
          <Filters
            filters={filters}
            setFilter={setFilter}
            enabledFilters={activeTabOptions?.filters}
            reportButton={activeTabOptions?.showSendReport && <SendReportButton />}
          />
        }
      />
      <div className={s.content}>
        {activeTab === ReportsTabs.Transactions && <TransactionsTable filters={filters} onRowClick={handleTransactionRowClick} />}
        {activeTab === ReportsTabs.Trades && <TradesTable filters={filters} onRowClick={handleTradeRowClick} />}
        {activeTab === ReportsTabs.Orders && <OrdersTable filters={filters} onRowClick={handleOrderRowClick} />}
        {activeTab === ReportsTabs.Analytics && <Analytics filters={filters} />}
        {openedSideBar && (
          <SideBar headerContent={openedSideBar.title} onClose={() => setOpenedSideBar(null)}>
            {openedSideBar.type === SideBarTypes.Trades && (
              <TradeInfo
                trade={openedSideBar.data.trade as MiniTrade}
                market={openedSideBar.data.market}
                onEditTrade={handleEditTrade}
              />
            )}
            {openedSideBar.type === SideBarTypes.Orders && (
              <TradeInfo
                trade={openedSideBar.data.trade as MiniOrder}
                market={openedSideBar.data.market}
                onEditTrade={handleEditOrder}
              />
            )}
            {openedSideBar.type === SideBarTypes.TradesEdit && (
              <EditTradeContainer order={openedSideBar.data.trade as MiniTrade} />
            )}
            {openedSideBar.type === SideBarTypes.OrdersEdit && (
              <EditOrderContainer order={openedSideBar.data.trade as MiniOrder} />
            )}
            {openedSideBar.type === SideBarTypes.Transactions && (
              <TransactionInfo transaction={openedSideBar.data as Transaction} />
            )}
          </SideBar>
        )}
      </div>
    </div>
  )
}
